import React, { useState, useEffect, useContext, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  makeStyles,
  CircularProgress,
  IconButton,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@material-ui/core";
import {
  PhotoCamera,
  Send,
  Delete,
  Videocam,
  Audiotrack,
  Schedule,
  Refresh,
  ChevronLeft,
  ChevronRight,
  History,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { socketConnection } from "../../services/socket";
import { i18n } from "../../translate/i18n";

const PRESET_COLORS = [
  "#000000", "#1a237e", "#0d47a1", "#00695c", "#00838f",
  "#6a1b9a", "#c62828", "#ef6c00", "#2e7d32",
  "#283593", "#388e3c", "#d32f2f", "#7b1fa2", "#0097a7", "#00bcd4"
];

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    padding: theme.spacing(3),
    backgroundColor: "#f5f7fb",
    ...theme.scrollbarStyles,
  },
  header: {
    textAlign: "center",
    marginBottom: theme.spacing(4),
  },
  headerTitle: {
    color: "#2196f3",
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  headerSubtitle: {
    color: "#666",
  },
  mainContainer: {
    display: "flex",
    gap: theme.spacing(4),
    flexWrap: "wrap",
    justifyContent: "center",
  },
  formContainer: {
    flex: "1 1 400px",
    maxWidth: 550,
  },
  previewContainer: {
    flex: "0 0 320px",
  },
  formPaper: {
    padding: theme.spacing(3),
    borderRadius: 16,
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: "#333",
    display: "flex",
    alignItems: "center",
    "&::before": {
      content: '""',
      width: 4,
      height: 20,
      backgroundColor: "#2196f3",
      marginRight: theme.spacing(1),
      borderRadius: 2,
    },
  },
  mediaButtons: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  mediaButton: {
    flex: 1,
    padding: theme.spacing(2),
    border: "2px dashed #ddd",
    borderRadius: 12,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    cursor: "pointer",
    transition: "all 0.3s",
    "&:hover": {
      borderColor: "#2196f3",
      backgroundColor: "rgba(33, 150, 243, 0.05)",
    },
  },
  mediaButtonActive: {
    borderColor: "#2196f3",
    backgroundColor: "rgba(33, 150, 243, 0.1)",
    borderStyle: "solid",
  },
  colorPalette: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  colorButton: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: "3px solid transparent",
    cursor: "pointer",
    transition: "all 0.2s",
    "&:hover": {
      transform: "scale(1.15)",
    },
  },
  colorButtonSelected: {
    border: "3px solid #fff",
    boxShadow: "0 0 0 2px #2196f3",
  },
  colorPickerButton: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)",
    cursor: "pointer",
    border: "2px solid #ddd",
  },
  scheduleSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: "#f9fafb",
    borderRadius: 12,
  },
  scheduleFields: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  submitButton: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    fontWeight: 600,
    textTransform: "none",
    fontSize: 16,
  },
  // Preview do celular
  phonePreview: {
    width: 280,
    height: 560,
    backgroundColor: "#000",
    borderRadius: 36,
    padding: 8,
    boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
    position: "relative",
  },
  phoneScreen: {
    width: "100%",
    height: "100%",
    backgroundColor: "#1a1a1a",
    borderRadius: 28,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  phoneHeader: {
    padding: theme.spacing(1, 2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    backgroundColor: "#1a1a1a",
  },
  phoneAvatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: "#2196f3",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: 600,
  },
  phoneContent: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
  },
  phoneStatusPreview: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    textAlign: "center",
  },
  phoneStatusText: {
    color: "#fff",
    fontSize: 18,
    fontWeight: 500,
    wordWrap: "break-word",
    maxWidth: "90%",
  },
  phoneStatusMedia: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  phoneFooter: {
    padding: theme.spacing(1, 2),
    backgroundColor: "rgba(0,0,0,0.5)",
  },
  phoneInput: {
    backgroundColor: "rgba(255,255,255,0.1)",
    borderRadius: 20,
    padding: theme.spacing(1, 2),
    color: "#999",
    fontSize: 14,
  },
  previewLabel: {
    textAlign: "center",
    marginTop: theme.spacing(2),
    color: "#666",
    fontWeight: 500,
  },
  // Histórico
  historySection: {
    marginTop: theme.spacing(4),
  },
  historyPaper: {
    padding: theme.spacing(3),
    borderRadius: 16,
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  },
  historyHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  historyTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 600,
    color: "#333",
  },
  historyCount: {
    backgroundColor: "#2196f3",
    color: "#fff",
    borderRadius: "50%",
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 600,
  },
  historyActions: {
    display: "flex",
    gap: theme.spacing(1),
  },
  historyGrid: {
    display: "flex",
    gap: theme.spacing(2),
    overflowX: "auto",
    paddingBottom: theme.spacing(1),
  },
  historyCard: {
    minWidth: 200,
    maxWidth: 200,
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  historyCardMedia: {
    height: 200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  historyCardText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: 500,
    textAlign: "center",
    wordWrap: "break-word",
  },
  historyCardContent: {
    padding: theme.spacing(1),
  },
  historyCardDate: {
    fontSize: 12,
    color: "#666",
  },
  republishButton: {
    borderRadius: 20,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 12,
  },
  emptyHistory: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: "#999",
  },
}));

const StatusPage = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const history = useHistory();
  
  // Verificar permissão
  useEffect(() => {
    if (user && user.allowWhatsappStatus !== "enabled") {
      toast.error(i18n.t("whatsappStatus.toasts.noPermission") || "Você não tem permissão para acessar esta página");
      history.push("/tickets");
    }
  }, [user, history]);
  
  // Estado para forçar re-renderização quando o idioma mudar
  const [, setLanguage] = useState(i18n.language);

  // Listener para mudança de idioma
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      setLanguage(lng);
    };
    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, []);
  
  // Estados do formulário
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
  const [text, setText] = useState("");
  const [backgroundColor, setBackgroundColor] = useState("#000000");
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Estados de agendamento
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [repeatType, setRepeatType] = useState("none");
  const [repeatEndDate, setRepeatEndDate] = useState("");
  
  // Estados do histórico
  const [statuses, setStatuses] = useState([]);
  const [statusesTotal, setStatusesTotal] = useState(0);
  const [statusesPage, setStatusesPage] = useState(1);
  const [loadingStatuses, setLoadingStatuses] = useState(false);

  // Carregar WhatsApps conectados (apenas Baileys, não API Oficial)
  useEffect(() => {
    const fetchWhatsapps = async () => {
      try {
        const { data } = await api.get("/whatsapp/");
        // Filtrar apenas conexões Baileys conectadas (excluir API Oficial)
        const connectedWhatsapps = data.filter(
          (w) => w.status === "CONNECTED" && w.channel !== "whatsapp_oficial"
        );
        setWhatsapps(connectedWhatsapps);
        if (connectedWhatsapps.length > 0) {
          setSelectedWhatsapp(connectedWhatsapps[0].id.toString());
        }
      } catch (err) {
        toast.error("Erro ao carregar WhatsApps");
      }
    };
    fetchWhatsapps();
  }, []);

  // Carregar histórico de status
  const fetchStatuses = useCallback(async () => {
    setLoadingStatuses(true);
    try {
      const { data } = await api.get("/whatsapp-status", {
        params: {
          whatsappId: selectedWhatsapp || undefined,
          page: statusesPage,
          limit: 10
        }
      });
      setStatuses(data.statuses || []);
      setStatusesTotal(data.total || 0);
    } catch (err) {
      console.error("Erro ao carregar histórico:", err);
    } finally {
      setLoadingStatuses(false);
    }
  }, [selectedWhatsapp, statusesPage]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  // Socket para atualizações em tempo real
  useEffect(() => {
    const companyId = user?.companyId;
    if (!companyId) return;

    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-whatsappStatus`, (data) => {
      if (data.action === "create" || data.action === "update") {
        fetchStatuses();
      } else if (data.action === "delete") {
        setStatuses((prev) => prev.filter((s) => s.id !== parseInt(data.statusId)));
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [user?.companyId, fetchStatuses]);

  // Handlers
  const handleMediaChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = type === "video" ? 16 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`${i18n.t("whatsappStatus.toasts.fileTooLarge")} ${maxSize / (1024 * 1024)}MB`);
        return;
      }
      setSelectedMedia(file);
      setMediaType(type);
      const reader = new FileReader();
      reader.onloadend = () => setMediaPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveMedia = () => {
    setSelectedMedia(null);
    setMediaPreview(null);
    setMediaType(null);
  };

  const handleSendStatus = async () => {
    if (!selectedWhatsapp) {
      toast.error(i18n.t("whatsappStatus.toasts.selectWhatsapp"));
      return;
    }
    if (!text && !selectedMedia) {
      toast.error(i18n.t("whatsappStatus.toasts.addContent"));
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("text", text);
      formData.append("backgroundColor", backgroundColor);
      formData.append("companyId", user.companyId);

      if (selectedMedia) {
        formData.append("media", selectedMedia);
      }

      if (isScheduled && scheduledDate && scheduledTime) {
        const scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`);
        formData.append("scheduledAt", scheduledAt.toISOString());
        formData.append("repeatType", repeatType);
        if (repeatEndDate) {
          formData.append("repeatEndAt", new Date(repeatEndDate).toISOString());
        }

        await api.post(`/whatsapp-status/${selectedWhatsapp}/schedule`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(i18n.t("whatsappStatus.toasts.scheduled"));
      } else {
        await api.post(`/whatsapp-status/${selectedWhatsapp}/send`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(i18n.t("whatsappStatus.toasts.success"));
      }
      
      // Limpar formulário
      setText("");
      setSelectedMedia(null);
      setMediaPreview(null);
      setMediaType(null);
      setBackgroundColor("#000000");
      setIsScheduled(false);
      setScheduledDate("");
      setScheduledTime("");
      setRepeatType("none");
      setRepeatEndDate("");
      
      fetchStatuses();
    } catch (err) {
      toast.error(err.response?.data?.error || i18n.t("whatsappStatus.toasts.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleRepublish = async (statusId) => {
    try {
      await api.post(`/whatsapp-status/${statusId}/republish`, {
        whatsappId: selectedWhatsapp
      });
      toast.success(i18n.t("whatsappStatus.toasts.republished"));
      fetchStatuses();
    } catch (err) {
      toast.error(err.response?.data?.error || i18n.t("whatsappStatus.toasts.error"));
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    return format(new Date(date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="h4" className={classes.headerTitle}>
          {i18n.t("whatsappStatus.title")}
        </Typography>
        <Typography variant="body1" className={classes.headerSubtitle}>
          {i18n.t("whatsappStatus.subtitle")}
      </Typography>
      </Box>

      <Box className={classes.mainContainer}>
        {/* Formulário */}
        <Box className={classes.formContainer}>
          <Paper className={classes.formPaper}>
            {/* Seleção de WhatsApp */}
            <Typography className={classes.sectionTitle}>
              {i18n.t("whatsappStatus.selectAccount")}
            </Typography>
        <TextField
          select
          fullWidth
          value={selectedWhatsapp}
          onChange={(e) => setSelectedWhatsapp(e.target.value)}
              SelectProps={{ native: true }}
          variant="outlined"
              size="small"
            >
              <option value="">{i18n.t("whatsappStatus.selectAccountPlaceholder")}</option>
              {whatsapps.map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
          ))}
        </TextField>

            {/* Upload de Mídia */}
      <Box mt={3}>
              <Typography className={classes.sectionTitle}>
                {i18n.t("whatsappStatus.uploadMedia")}
        </Typography>
              <Box className={classes.mediaButtons}>
        <input
                  accept="image/*"
          style={{ display: "none" }}
                  id="image-upload"
          type="file"
                  onChange={(e) => handleMediaChange(e, "image")}
                />
                <label htmlFor="image-upload" style={{ flex: 1 }}>
                  <Box 
                    className={`${classes.mediaButton} ${mediaType === "image" ? classes.mediaButtonActive : ""}`}
            component="span"
                  >
                    <PhotoCamera style={{ fontSize: 28, color: mediaType === "image" ? "#2196f3" : "#666" }} />
                    <Typography variant="caption" style={{ marginTop: 4 }}>{i18n.t("whatsappStatus.image")}</Typography>
                  </Box>
        </label>

                <input
                  accept="video/*"
                  style={{ display: "none" }}
                  id="video-upload"
                  type="file"
                  onChange={(e) => handleMediaChange(e, "video")}
                />
                <label htmlFor="video-upload" style={{ flex: 1 }}>
                  <Box 
                    className={`${classes.mediaButton} ${mediaType === "video" ? classes.mediaButtonActive : ""}`}
                    component="span"
                  >
                    <Videocam style={{ fontSize: 28, color: mediaType === "video" ? "#2196f3" : "#666" }} />
                    <Typography variant="caption" style={{ marginTop: 4 }}>{i18n.t("whatsappStatus.video")}</Typography>
                  </Box>
                </label>

                <input
                  accept="audio/*"
                  style={{ display: "none" }}
                  id="audio-upload"
                  type="file"
                  onChange={(e) => handleMediaChange(e, "audio")}
                />
                <label htmlFor="audio-upload" style={{ flex: 1 }}>
                  <Box 
                    className={`${classes.mediaButton} ${mediaType === "audio" ? classes.mediaButtonActive : ""}`}
                    component="span"
                  >
                    <Audiotrack style={{ fontSize: 28, color: mediaType === "audio" ? "#2196f3" : "#666" }} />
                    <Typography variant="caption" style={{ marginTop: 4 }}>{i18n.t("whatsappStatus.audio")}</Typography>
                  </Box>
                </label>
              </Box>

              {selectedMedia && (
                <Box mt={2} display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" color="textSecondary">
                    {selectedMedia.name}
                  </Typography>
                  <IconButton size="small" onClick={handleRemoveMedia}>
                    <Delete fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

            {/* Texto do Status */}
            <Box mt={3}>
              <Typography className={classes.sectionTitle}>
                {i18n.t("whatsappStatus.statusText")}
        </Typography>
        <TextField
          fullWidth
          multiline
                rows={3}
                placeholder={i18n.t("whatsappStatus.statusTextPlaceholder")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          variant="outlined"
                disabled={mediaType === "audio"}
              />
            </Box>

            {/* Cor de Fundo */}
            {!selectedMedia && (
              <Box mt={3}>
                <Typography className={classes.sectionTitle}>
                  {i18n.t("whatsappStatus.backgroundColor")}
                </Typography>
                <Box className={classes.colorPalette}>
                  {PRESET_COLORS.map((color) => (
                    <Box
                      key={color}
                      className={`${classes.colorButton} ${backgroundColor === color ? classes.colorButtonSelected : ""}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setBackgroundColor(color)}
                    />
                  ))}
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    style={{ 
                      width: 36, 
                      height: 36, 
                      borderRadius: "50%", 
                      border: "none",
                      cursor: "pointer",
                      padding: 0
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Agendamento */}
            <Box className={classes.scheduleSection}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isScheduled}
                    onChange={(e) => setIsScheduled(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Schedule fontSize="small" />
                    <Typography>{i18n.t("whatsappStatus.scheduleForLater")}</Typography>
                  </Box>
                }
              />

              {isScheduled && (
                <Box className={classes.scheduleFields}>
                  <TextField
                    label={i18n.t("whatsappStatus.date")}
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="small"
                    style={{ flex: 1 }}
                  />
                  <TextField
                    label={i18n.t("whatsappStatus.time")}
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="small"
                    style={{ flex: 1 }}
                  />
                  <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
                    <InputLabel>{i18n.t("whatsappStatus.repeat")}</InputLabel>
                    <Select
                      value={repeatType}
                      onChange={(e) => setRepeatType(e.target.value)}
                      label={i18n.t("whatsappStatus.repeat")}
                    >
                      <MenuItem value="none">{i18n.t("whatsappStatus.noRepeat")}</MenuItem>
                      <MenuItem value="daily">{i18n.t("whatsappStatus.daily")}</MenuItem>
                      <MenuItem value="weekly">{i18n.t("whatsappStatus.weekly")}</MenuItem>
                      <MenuItem value="monthly">{i18n.t("whatsappStatus.monthly")}</MenuItem>
                    </Select>
                  </FormControl>
                  {repeatType !== "none" && (
                    <TextField
                      label={i18n.t("whatsappStatus.endDate")}
                      type="date"
                      value={repeatEndDate}
                      onChange={(e) => setRepeatEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                      size="small"
                      style={{ flex: 1 }}
                    />
            )}
          </Box>
        )}
      </Box>

            {/* Botão de Enviar */}
        <Button
              fullWidth
          variant="contained"
          color="primary"
              className={classes.submitButton}
          onClick={handleSendStatus}
          disabled={loading || (!text && !selectedMedia)}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : (isScheduled ? <Schedule /> : <Send />)}
        >
              {loading ? i18n.t("whatsappStatus.sending") : (isScheduled ? i18n.t("whatsappStatus.scheduleStatus") : i18n.t("whatsappStatus.sendStatus"))}
        </Button>
          </Paper>
        </Box>

        {/* Preview do Celular */}
        <Box className={classes.previewContainer}>
          <Box className={classes.phonePreview}>
            <Box className={classes.phoneScreen}>
              {/* Header do Status */}
              <Box className={classes.phoneHeader}>
                <Box className={classes.phoneAvatar}>W</Box>
                <Box>
                  <Typography style={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>
                    {i18n.t("whatsappStatus.myStatus")}
                  </Typography>
                  <Typography style={{ color: "#999", fontSize: 12 }}>
                    {i18n.t("whatsappStatus.justNow")}
                  </Typography>
                </Box>
              </Box>

              {/* Conteúdo do Status */}
              <Box 
                className={classes.phoneContent}
                style={{ 
                  backgroundColor: !selectedMedia ? backgroundColor : "#000"
                }}
              >
                {mediaPreview ? (
                  mediaType === "image" ? (
                    <img 
                      src={mediaPreview} 
                      alt="Preview" 
                      className={classes.phoneStatusMedia}
                    />
                  ) : mediaType === "video" ? (
                    <video 
                      src={mediaPreview} 
                      className={classes.phoneStatusMedia}
                      style={{ objectFit: "contain" }}
                    />
                  ) : mediaType === "audio" ? (
                    <Box className={classes.phoneStatusPreview}>
                      <Audiotrack style={{ fontSize: 64, color: "#fff" }} />
                    </Box>
                  ) : null
                ) : text ? (
                  <Box className={classes.phoneStatusPreview}>
                    <Typography className={classes.phoneStatusText}>
                      {text}
                    </Typography>
                  </Box>
                ) : (
                  <Box className={classes.phoneStatusPreview} style={{ backgroundColor: "#1a1a1a" }}>
                    <Box textAlign="center">
                      <Typography style={{ color: "#666", fontSize: 48 }}>+</Typography>
                      <Typography style={{ color: "#666", fontSize: 14 }}>
                        {i18n.t("whatsappStatus.previewPlaceholder")}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Caption overlay */}
                {selectedMedia && text && mediaType !== "audio" && (
                  <Box 
                    style={{
                      position: "absolute",
                      bottom: 60,
                      left: 0,
                      right: 0,
                      padding: "12px 16px",
                      backgroundColor: "rgba(0,0,0,0.6)",
                    }}
                  >
                    <Typography style={{ color: "#fff", fontSize: 14 }}>
                      {text}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Footer */}
              <Box className={classes.phoneFooter}>
                <Box className={classes.phoneInput}>
                  {i18n.t("whatsappStatus.replyToStatus")}
                </Box>
              </Box>
            </Box>
          </Box>
          <Typography className={classes.previewLabel}>
            {i18n.t("whatsappStatus.preview")}
          </Typography>
        </Box>
      </Box>

      {/* Histórico de Status */}
      <Box className={classes.historySection}>
        <Paper className={classes.historyPaper}>
          <Box className={classes.historyHeader}>
            <Box className={classes.historyTitle}>
              <History />
              <Typography variant="h6">{i18n.t("whatsappStatus.previousStatuses")}</Typography>
              <Box className={classes.historyCount}>{statusesTotal}</Box>
            </Box>
            <Box className={classes.historyActions}>
              <IconButton size="small" onClick={fetchStatuses} disabled={loadingStatuses}>
                <Refresh />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={() => setStatusesPage((p) => Math.max(1, p - 1))}
                disabled={statusesPage <= 1}
              >
                <ChevronLeft />
              </IconButton>
              <IconButton 
                size="small"
                onClick={() => setStatusesPage((p) => p + 1)}
                disabled={statuses.length < 10}
              >
                <ChevronRight />
              </IconButton>
            </Box>
          </Box>

          <Divider style={{ marginBottom: 16 }} />

          {loadingStatuses ? (
            <Box textAlign="center" py={4}>
              <CircularProgress />
            </Box>
          ) : statuses.length === 0 ? (
            <Box className={classes.emptyHistory}>
              <History style={{ fontSize: 48, marginBottom: 8 }} />
              <Typography>{i18n.t("whatsappStatus.noStatuses")}</Typography>
            </Box>
          ) : (
            <Box className={classes.historyGrid}>
              {statuses.map((status) => (
                <Card key={status.id} className={classes.historyCard}>
                  <Box 
                    className={classes.historyCardMedia}
                    style={{ 
                      backgroundColor: status.mediaUrl ? "#000" : (status.backgroundColor || "#000")
                    }}
                  >
                    {status.mediaUrl ? (
                      status.mediaType === "image" ? (
                        <img 
                          src={status.mediaUrl} 
                          alt="Status" 
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : status.mediaType === "video" ? (
                        <Box style={{ position: "relative", width: "100%", height: "100%" }}>
                          <video 
                            src={status.mediaUrl}
                            style={{ 
                              width: "100%", 
                              height: "100%", 
                              objectFit: "cover" 
                            }}
                            preload="metadata"
                            muted
                          />
                          {/* Ícone de play sobreposto */}
                          <Box
                            style={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              backgroundColor: "rgba(0, 0, 0, 0.6)",
                              borderRadius: "50%",
                              width: 48,
                              height: 48,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              pointerEvents: "none"
                            }}
                          >
                            <Videocam style={{ fontSize: 24, color: "#fff" }} />
                          </Box>
                        </Box>
                      ) : (
                        <Audiotrack style={{ fontSize: 48, color: "#fff" }} />
                      )
                    ) : (
                      <Typography className={classes.historyCardText}>
                        {status.text?.substring(0, 100)}
                        {status.text?.length > 100 ? "..." : ""}
                      </Typography>
                    )}
                  </Box>
                  <CardContent className={classes.historyCardContent}>
                    <Typography className={classes.historyCardDate}>
                      {formatDate(status.lastSentAt || status.createdAt)}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      style={{ 
                        color: status.status === "sent" ? "#4caf50" : 
                               status.status === "failed" ? "#f44336" : 
                               status.status === "scheduled" ? "#ff9800" : "#999"
                      }}
                    >
                      {status.status === "sent" ? i18n.t("whatsappStatus.sent") :
                       status.status === "failed" ? i18n.t("whatsappStatus.failed") :
                       status.status === "scheduled" ? i18n.t("whatsappStatus.scheduled") : status.status}
                    </Typography>
                  </CardContent>
                  <CardActions style={{ padding: "0 8px 8px" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      className={classes.republishButton}
                      onClick={() => handleRepublish(status.id)}
                      disabled={!selectedWhatsapp}
                    >
                      {i18n.t("whatsappStatus.republish")}
                    </Button>
                  </CardActions>
                </Card>
              ))}
            </Box>
          )}
    </Paper>
      </Box>
    </Box>
  );
};

export default StatusPage;
